import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Mail, Shield, AlertCircle, Building2, ArrowLeft, Info } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import EntraIdConfig, { isEnterpriseDomain } from '../config/entraId';
import { useTenant } from '../contexts/TenantContext';
import { API_BASE } from '../config/api';

const API_URL = API_BASE;

// Microsoft Logo SVG
const MicrosoftLogo = () => (
  <svg className="h-5 w-5" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
  </svg>
);

export default function EnterpriseSSOPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPlaceholderWarning, setShowPlaceholderWarning] = useState(false);
  
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';
  
  // Check if using placeholder config
  const isPlaceholder = EntraIdConfig.isPlaceholder();

  const handleSSOInitiate = (e) => {
    e.preventDefault();
    setError('');

    // SSO is resolved per tenant from the slug, not from the email domain.
    // The old email -> tenant lookup lived in routes/enterprise_auth.py, which
    // now registers no routes, so posting an email here failed with a 404.
    if (!tenant?.slug) {
      setError(
        "Please use your organisation's Cerebro link to sign in. " +
        'Contact your administrator if you do not have it.'
      );
      return;
    }

    if (email.trim() && !isEnterpriseDomain(email)) {
      setError('Please use your corporate email address. Personal email domains are not supported for enterprise SSO.');
      return;
    }

    setLoading(true);
    sessionStorage.setItem('sso_return_path', from);

    // Full-page navigation: the endpoint answers with a 302 to Microsoft.
    window.location.href =
      `${API_URL}/api/sso/login/${tenant.slug}?redirect_to=${encodeURIComponent(from)}`;
  };

  const handleDemoMode = () => {
    setShowPlaceholderWarning(false);
    toast.info('Demo mode: SSO would redirect to Microsoft login in production', {
      duration: 4000,
      description: 'Configure Azure AD credentials to enable real SSO'
    });
  };

  return (
    <div className="min-h-screen bg-navigate-dark flex flex-col" data-testid="enterprise-sso-page">
      <div className="flex flex-1 items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md">
          {/* Back to Login Link */}
          <Link 
            to="/login" 
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to standard login
          </Link>
          
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600/20 rounded-full mb-4">
              <Building2 className="h-8 w-8 text-blue-400" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-white mb-2">
              Enterprise Sign In
            </h1>
            <p className="text-zinc-400">
              Sign in with your organization&apos;s Microsoft account
            </p>
          </div>
          
          {/* Placeholder Warning Modal */}
          {showPlaceholderWarning && (
            <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
                <div>
                  <h3 className="font-medium text-amber-400 mb-1">Demo Mode Active</h3>
                  <p className="text-sm text-amber-300/80 mb-3">
                    Microsoft Entra ID is configured with placeholder credentials. 
                    To enable real SSO authentication:
                  </p>
                  <ol className="text-sm text-amber-300/80 list-decimal list-inside space-y-1 mb-4">
                    <li>Register an app in Azure Portal</li>
                    <li>Configure redirect URI</li>
                    <li>Add Client ID & Secret to environment</li>
                  </ol>
                  <div className="flex gap-2">
                    <button
                      onClick={handleDemoMode}
                      className="px-3 py-1.5 bg-amber-600/20 hover:bg-amber-600/30 text-amber-300 rounded text-sm transition-colors"
                    >
                      Continue in Demo Mode
                    </button>
                    <button
                      onClick={() => setShowPlaceholderWarning(false)}
                      className="px-3 py-1.5 text-zinc-400 hover:text-white text-sm transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Error Alert */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-3 text-red-400">
              <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
              <span className="text-sm">{error}</span>
            </div>
          )}
          
          {/* SSO Form */}
          <form onSubmit={handleSSOInitiate} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-300">Corporate Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@yourcompany.com"
                  required
                  className="pl-10 bg-navigate-surface border-zinc-800 text-white placeholder:text-zinc-500 focus:border-blue-500 focus:ring-blue-500"
                  data-testid="sso-email-input"
                />
              </div>
              <p className="text-xs text-zinc-500">
                Enter your corporate email to sign in via Microsoft Entra ID
              </p>
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-[#0078D4] hover:bg-[#106EBE] text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="sso-submit-btn"
            >
              {loading ? (
                <>
                  <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Connecting...</span>
                </>
              ) : (
                <>
                  <MicrosoftLogo />
                  <span>Sign in with Microsoft</span>
                </>
              )}
            </button>
          </form>
          
          {/* Info Section */}
          <div className="mt-8 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-blue-400 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-white text-sm mb-1">Enterprise Security</h3>
                <p className="text-xs text-zinc-400">
                  Your organization uses Microsoft Entra ID (Azure AD) for secure single sign-on. 
                  You&apos;ll be redirected to your organization&apos;s login page.
                </p>
              </div>
            </div>
          </div>
          
          {/* Contact Admin */}
          <p className="mt-6 text-center text-sm text-zinc-500">
            Don&apos;t have access? <a href="mailto:support@navigatelearning.io" className="text-navigate-gold hover:underline">Contact your administrator</a>
          </p>
          
          {/* Placeholder Badge */}
          {isPlaceholder && (
            <div className="mt-4 flex justify-center">
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-500/10 border border-amber-500/30 rounded text-xs text-amber-400">
                <Info className="h-3 w-3" />
                Demo Mode - Configure Azure AD to enable
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
